import { useState } from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProjectionControls from "../components/projections/ProjectionControls";
import MetricsDisplay from "../components/projections/MetricsDisplay";
import ProjectionCharts from "../components/projections/ProjectionCharts";
import { calculateProjections, AssumptionInputs } from "../utils/projectionCalculations";

const Projections = () => {
  const [numberOfPods, setNumberOfPods] = useState(45);
  const [pricePerHour, setPricePerHour] = useState(8);
  const [utilization, setUtilization] = useState(37);
  
  const [assumptions, setAssumptions] = useState<AssumptionInputs>({
    hoursPerMonth: 730,
    revenueSharePercent: 50,
    maintenanceCostPerPod: 50,
    insuranceCostPerPod: 20,
    paymentProcessingPercent: 2.5,
    fixedOverhead: 20000,
    initialPodCost: 1120,
  });

  const projections = calculateProjections(numberOfPods, pricePerHour, utilization, assumptions);

  const handleReset = () => {
    setNumberOfPods(45);
    setPricePerHour(8);
    setUtilization(37);
    setAssumptions({
      hoursPerMonth: 730,
      revenueSharePercent: 50,
      maintenanceCostPerPod: 50,
      insuranceCostPerPod: 20,
      paymentProcessingPercent: 2.5,
      fixedOverhead: 20000,
      initialPodCost: 1120,
    });
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-cream pt-24 pb-16">
        <div className="mx-auto px-4 desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="font-funnel font-bold text-5xl text-foreground mb-4">
              Model Your Growth
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              See how Qwip's economics scale with real-time calculations. Adjust the sliders to explore different scenarios.
            </p>
          </motion.div>

          {/* Main Dashboard Grid */}
          <div className="grid desktop:grid-cols-12 laptop:grid-cols-12 tablet:grid-cols-1 mobile:grid-cols-1 gap-8">
            {/* Controls Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="desktop:col-span-4 laptop:col-span-4"
            >
              <ProjectionControls
                numberOfPods={numberOfPods}
                setNumberOfPods={setNumberOfPods}
                pricePerHour={pricePerHour}
                setPricePerHour={setPricePerHour}
                utilization={utilization}
                setUtilization={setUtilization}
                onReset={handleReset}
              />
            </motion.div>

            {/* Metrics Display */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="desktop:col-span-8 laptop:col-span-8"
            >
              <MetricsDisplay projections={projections} />
            </motion.div>
          </div>

          {/* Charts Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8"
          >
            <ProjectionCharts 
              projections={projections} 
              numberOfPods={numberOfPods} 
              assumptions={assumptions}
              setAssumptions={setAssumptions}
            />
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 text-center text-sm text-muted-foreground max-w-4xl mx-auto"
          >
            <p>
              These projections are estimates based on target metrics. Actual results will vary based on location performance, market conditions, and operational execution.
            </p>
          </motion.div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Projections;
